import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, gbp, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Archive, Trash2, Upload, Loader2, Package, X, Image as ImageIcon, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DetailPanel } from "@/admin/DetailPanel";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const BLANK = {
  sku: "", name: "", category: "Snacks", description: "", image_url: "", images: [], barcode: "",
  cost_price: 0, selling_price: 0, vat_rate: 20, status: "active", active: true, stock_controlled: true, pack_size: 1,
};
const STATUS_OPTS = [["active", "Active"], ["draft", "Draft"], ["unlisted", "Unlisted"], ["inactive", "Inactive"]];
const STATUS_BADGE = {
  active: { label: "Active", variant: "default" },
  draft: { label: "Draft", variant: "secondary" },
  unlisted: { label: "Unlisted", variant: "outline" },
  inactive: { label: "Inactive", variant: "secondary" },
};

function ImageManager({ images, mainUrl, onChange, testid = "img" }) {
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [lib, setLib] = useState([]);
  const list = images || [];
  const main = mainUrl || list[0] || "";
  const backend = process.env.REACT_APP_BACKEND_URL;
  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 6 - list.length;
    if (room <= 0) { toast.error("Maximum 6 images"); return; }
    setBusy(true);
    const added = [];
    for (const f of files.slice(0, room)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(`${backend}${res.data.url}`);
      } catch { toast.error("Upload failed"); }
    }
    const next = [...list, ...added];
    onChange(next, main || next[0] || "");
    setBusy(false);
  };
  const [libQuery, setLibQuery] = useState("");
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem("hv_recent_media") || "[]"); } catch { return []; } });
  const openLib = async () => {
    setLibOpen(true);
    setLibQuery("");
    try { const r = await api.get("/media"); setLib(r.data); } catch { setLib([]); }
  };
  const pick = (url) => {
    const abs = `${backend}${url}`;
    if (list.includes(abs)) { setLibOpen(false); return; }
    if (list.length >= 6) { toast.error("Maximum 6 images"); return; }
    const next = [...list, abs];
    onChange(next, main || next[0]);
    const nr = [url, ...recent.filter((u) => u !== url)].slice(0, 12);
    setRecent(nr);
    try { localStorage.setItem("hv_recent_media", JSON.stringify(nr)); } catch { /* ignore */ }
    setLibOpen(false);
  };
  const remove = (url) => {
    const next = list.filter((u) => u !== url);
    onChange(next, main === url ? (next[0] || "") : main);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid={`${testid}-gallery`}>
        {list.map((url) => (
          <div key={url} className={`relative h-20 w-20 rounded-lg overflow-hidden border-2 ${main === url ? "border-blue-600" : "border-slate-200 dark:border-slate-700"}`}>
            <img src={url} alt="" className="h-full w-full object-cover cursor-pointer" onClick={() => onChange(list, url)} data-testid={`${testid}-thumb`} title="Set as main image" />
            {main === url && <span className="absolute bottom-0 inset-x-0 bg-blue-600 text-white text-[9px] font-bold text-center">MAIN</span>}
            <button type="button" onClick={() => remove(url)} data-testid={`${testid}-remove`} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {list.length < 6 && (
          <label className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 grid place-items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`${testid}-upload`}>
            <input type="file" accept="image/*" multiple className="hidden" onChange={addFiles} />
            {busy ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : <Upload className="h-5 w-5 text-slate-400" />}
          </label>
        )}
        {list.length < 6 && (
          <button type="button" onClick={openLib} data-testid={`${testid}-library`} className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 grid place-items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 text-[10px] font-semibold text-slate-400">
            <span className="flex flex-col items-center"><ImageIcon className="h-4 w-4 mb-0.5" />Library</span>
          </button>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-1.5">Upload new images or pick from your Media library. Click an image to set it as the main shelf image. Up to 6.</p>
      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent style={{ zIndex: 120 }} className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Media library</DialogTitle></DialogHeader>
          <Input data-testid={`${testid}-library-search`} value={libQuery} onChange={(e) => setLibQuery(e.target.value)} placeholder="Search images by filename…" className="mb-3" />
          {recent.length > 0 && !libQuery && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 mb-1.5">Recently used</p>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {recent.slice(0, 10).map((url) => (
                  <button key={url} type="button" onClick={() => pick(url)} data-testid={`${testid}-library-recent`} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-blue-500">
                    <img src={`${backend}${url}`} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid={`${testid}-library-grid`}>
            {lib.filter((m) => !libQuery || (m.filename || "").toLowerCase().includes(libQuery.toLowerCase())).map((m) => (
              <button key={m.id} type="button" onClick={() => pick(m.url)} data-testid={`${testid}-library-pick`} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-blue-500 transition-colors">
                <img src={`${backend}${m.url}`} alt={m.filename} className="h-full w-full object-cover" loading="lazy" />
              </button>
            ))}
            {!lib.filter((m) => !libQuery || (m.filename || "").toLowerCase().includes(libQuery.toLowerCase())).length && <p className="col-span-full text-sm text-slate-400 py-6 text-center">No images match.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [sp, setSp] = useSearchParams();
  const catFilter = sp.get("category") || "";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(BLANK);
  const [savingEdit, setSavingEdit] = useState(false);
  const [importing, setImporting] = useState(false);
  const [stdVat, setStdVat] = useState(20);
  const [vatOpen, setVatOpen] = useState(false);
  const [vatDraft, setVatDraft] = useState("20");
  const [vatSaving, setVatSaving] = useState(false);
  useEffect(() => { api.get("/settings/vat").then((r) => { setStdVat(r.data.standard_vat_rate); setVatDraft(String(r.data.standard_vat_rate)); }).catch(() => {}); }, []);
  const vatMode = (v) => (Number(v) === 0 ? "zero" : "standard");
  const setVat = (mode, isEdit) => { const rate = mode === "zero" ? 0 : stdVat; (isEdit ? setEditForm : setForm)((f) => ({ ...f, vat_mode: mode, vat_rate: rate })); };
  const saveVat = async () => { setVatSaving(true); try { const r = await api.put("/settings/vat", { standard_vat_rate: Number(vatDraft) || 0 }); setStdVat(r.data.standard_vat_rate); toast.success("Standard VAT updated — standard-rated products re-costed"); setVatOpen(false); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setVatSaving(false); } };
  const eupd = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const editIncl = (Number(editForm.selling_price) * (1 + Number(editForm.vat_rate) / 100)).toFixed(2);
  const openView = (p) => { setSelected(p); setEditing(false); };
  const openInlineEdit = (p) => { setSelected(p); setEditForm({ ...BLANK, ...p }); setEditing(true); };
  const startEdit = () => { setEditForm({ ...BLANK, ...selected }); setEditing(true); };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = { ...editForm, cost_price: Number(editForm.cost_price), selling_price: Number(editForm.selling_price), vat_rate: Number(editForm.vat_rate), vat_mode: vatMode(editForm.vat_rate), pack_size: Number(editForm.pack_size) || 1 };
      const r = await api.put(`/products/${selected.id}`, payload);
      toast.success("Product updated");
      setSelected(r.data);
      setEditing(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);
  const [categories, setCategories] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  const catOptions = (current) => Array.from(new Set([...(current ? [current] : []), ...categories.map((c) => c.name)]));
  const [view, setView] = useState("active");
  const [deleted, setDeleted] = useState([]);
  const loadDeleted = () => api.get("/products/deleted").then((r) => setDeleted(r.data)).catch(() => setDeleted([]));
  useEffect(() => { loadDeleted(); }, []);
  useEffect(() => { if (view === "deleted") loadDeleted(); }, [view]);
  const activeCount = products.filter((p) => !p.archived).length;
  const reinstate = async (p) => {
    try { await api.post(`/products/${p.id}/restore`); toast.success("Reinstated as Unlisted"); loadDeleted(); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [toClear, setToClear] = useState(null);
  const clearOne = async () => {
    if (!toClear) return;
    try { await api.delete(`/products/${toClear.id}/permanent`); toast.success("Permanently deleted"); setToClear(null); loadDeleted(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const clearAll = async () => {
    try { const r = await api.post("/products/deleted/clear-all"); toast.success(`Cleared ${r.data.cleared} product(s)`); setClearAllOpen(false); loadDeleted(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- Stock by kiosk (managed from the product panel) ----
  const [kioskStock, setKioskStock] = useState([]);
  const [allKiosks, setAllKiosks] = useState([]);
  const [lines, setLines] = useState({});
  const [addForm, setAddForm] = useState({ kiosk_id: "", cases: 0, loose: 0, par: 12 });

  const refreshKioskStock = (pid, packSize) => api.get(`/products/${pid}/kiosk-stock`).then((r) => {
    setKioskStock(r.data);
    const m = {};
    r.data.forEach((row) => { m[row.kiosk_id] = { cases: Math.floor(row.stock_qty / packSize), loose: row.stock_qty % packSize, par: row.par_level ?? 0 }; });
    setLines(m);
  });

  useEffect(() => {
    if (selected && !editing) {
      refreshKioskStock(selected.id, selected.pack_size || 1);
      if (!allKiosks.length) api.get("/kiosks").then((r) => setAllKiosks(r.data)).catch(() => {});
    }
  }, [selected, editing]);

  const setLine = (kid, k, v) => setLines((L) => ({ ...L, [kid]: { ...L[kid], [k]: v } }));

  const saveLine = async (kid) => {
    const l = lines[kid]; const pk = selected.pack_size || 1;
    const total = (Number(l.cases) || 0) * pk + (Number(l.loose) || 0);
    try {
      await api.post("/stock/adjust", { kiosk_id: kid, product_id: selected.id, new_qty: total, reason: "stocktake", par_level: Number(l.par) || 0 });
      toast.success("Kiosk stock updated");
      refreshKioskStock(selected.id, pk);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addToKiosk = async () => {
    if (!addForm.kiosk_id) return;
    const pk = selected.pack_size || 1;
    const total = (Number(addForm.cases) || 0) * pk + (Number(addForm.loose) || 0);
    try {
      await api.post("/kiosk-products", { kiosk_id: addForm.kiosk_id, product_id: selected.id, stock_qty: total, par_level: Number(addForm.par) || 0, active: true });
      toast.success("Added to kiosk");
      setAddForm({ kiosk_id: "", cases: 0, loose: 0, par: 12 });
      refreshKioskStock(selected.id, pk);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const unassignedKiosks = allKiosks.filter((k) => !kioskStock.find((r) => r.kiosk_id === k.id));

  const openNew = () => { setForm(BLANK); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...BLANK, ...p });
    setEditId(p.id);
    setOpen(true);
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inclPreview = (Number(form.selling_price) * (1 + Number(form.vat_rate) / 100)).toFixed(2);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_price: Number(form.cost_price),
        selling_price: Number(form.selling_price),
        vat_rate: Number(form.vat_rate),
        vat_mode: vatMode(form.vat_rate),
        pack_size: Number(form.pack_size) || 1,
      };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const archive = async (p) => {
    await api.post(`/products/${p.id}/archive`);
    toast.success("Product archived");
    load();
  };

  const [toDelete, setToDelete] = useState(null);
  const performDelete = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
      load();
      loadDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };
  const confirmDelete = () => {
    const p = toDelete;
    if (!p) return;
    setToDelete(null);
    // Optimistically remove from the list; actually delete after a 6s undo window.
    setProducts((prev) => prev.filter((x) => x.id !== p.id));
    if (selected?.id === p.id) setSelected(null);
    let undone = false;
    const timer = setTimeout(() => { if (!undone) performDelete(p); }, 6000);
    toast(`Deleted “${p.name}”`, {
      duration: 6000,
      action: {
        label: "Undo",
        onClick: () => { undone = true; clearTimeout(timer); load(); toast.success("Delete cancelled"); },
      },
    });
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} new, ${r.data.updated} updated${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Products</h1>
          <p className="text-slate-500 mt-1">{activeCount} active · {deleted.length} deleted</p>
        </div>
        <div className="flex items-center">
          <Button data-testid="vat-settings-open" variant="outline" onClick={() => setVatOpen(true)} className="mr-2 h-10">VAT settings</Button>
          <Dialog open={vatOpen} onOpenChange={setVatOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>Standard VAT rate</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500 -mt-1">Applies to every “Standard VAT” product. Changing it re-costs them automatically. Super admin only.</p>
              <div><Label>Standard VAT %</Label><Input data-testid="vat-settings-input" type="number" step="0.1" value={vatDraft} onChange={(e) => setVatDraft(e.target.value)} className="mt-1" /></div>
              <DialogFooter><Button variant="outline" onClick={() => setVatOpen(false)}>Cancel</Button><Button data-testid="vat-settings-save" onClick={saveVat} disabled={vatSaving} className="bg-blue-600 hover:bg-blue-700">{vatSaving ? "Saving…" : "Save"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <label data-testid="product-import-button">
            <input type="file" accept=".xlsx" className="hidden" onChange={doImport} />
            <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 px-3 h-10 text-sm font-medium cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 mr-2">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Import from Square
            </span>
          </label>
          <Button data-testid="product-new-button" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> New product</Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button data-testid="products-view-active" onClick={() => setView("active")} className={`px-3 h-9 rounded-md text-sm font-medium border ${view === "active" ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}>Products ({activeCount})</button>
        <button data-testid="products-view-deleted" onClick={() => setView("deleted")} className={`px-3 h-9 rounded-md text-sm font-medium border ${view === "deleted" ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}>Deleted ({deleted.length})</button>
        {catFilter && (
          <button data-testid="products-clear-category" onClick={() => { sp.delete("category"); setSp(sp, { replace: true }); }} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300">
            Category: {catFilter} <span className="text-indigo-400">✕</span>
          </button>
        )}
      </div>

      {view === "active" && (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Sell (excl)</TableHead>
              <TableHead className="text-right">VAT</TableHead>
              <TableHead className="text-right">Incl VAT</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.filter((p) => !p.archived && (!catFilter || p.category === catFilter)).map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.sku}`} onClick={() => openView(p)} className="cursor-pointer">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0">
                      {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" /> : <Package className="h-5 w-5 m-2.5 text-slate-400" />}
                    </div>
                    <span className="font-medium text-slate-900 dark:text-white">{p.name}</span>
                  </div>
                </TableCell>
                <TableCell className="font-mono-x text-xs">{p.sku}</TableCell>
                <TableCell>{p.category}</TableCell>
                <TableCell className="text-right">{gbp(p.cost_price)}</TableCell>
                <TableCell className="text-right">{gbp(p.selling_price)}</TableCell>
                <TableCell className="text-right">{p.vat_rate}%</TableCell>
                <TableCell className="text-right font-semibold">{gbp(p.price_incl_vat)}</TableCell>
                <TableCell>{(() => { const s = STATUS_BADGE[p.status] || STATUS_BADGE[p.active ? "active" : "inactive"]; return <Badge variant={s.variant}>{s.label}</Badge>; })()}</TableCell>
                <TableCell className="text-right">
                  <button data-testid={`product-edit-${p.sku}`} onClick={(e) => { e.stopPropagation(); openInlineEdit(p); }} className="text-slate-400 hover:text-blue-600 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`product-archive-${p.sku}`} onClick={(e) => { e.stopPropagation(); archive(p); }} className="text-slate-400 hover:text-amber-600 p-1.5" title="Archive"><Archive className="h-4 w-4" /></button>
                  <button data-testid={`product-delete-${p.sku}`} onClick={(e) => { e.stopPropagation(); setToDelete(p); }} className="text-slate-400 hover:text-red-600 p-1.5" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}

      {view === "deleted" && (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button data-testid="products-clear-all" variant="outline" onClick={() => setClearAllOpen(true)} disabled={!deleted.length} className="text-red-600 border-red-200 hover:bg-red-50">Clear all</Button>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Deleted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!deleted.length && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No deleted products.</TableCell></TableRow>}
              {deleted.map((p) => (
                <TableRow key={p.id} data-testid={`deleted-row-${p.sku}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0">
                        {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" /> : <Package className="h-5 w-5 m-2.5 text-slate-400" />}
                      </div>
                      <span className="font-medium text-slate-900 dark:text-white">{p.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono-x text-xs">{p.sku}</TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell className="text-sm text-slate-500">{p.deleted_at ? new Date(p.deleted_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`product-reinstate-${p.sku}`} variant="outline" size="sm" onClick={() => reinstate(p)} className="mr-2">Reinstate</Button>
                    <Button data-testid={`product-clear-${p.sku}`} variant="outline" size="sm" onClick={() => setToClear(p)} className="text-red-600 border-red-200 hover:bg-red-50">Clear</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      )}

      <DetailPanel
        open={!!selected}
        onClose={() => { setSelected(null); setEditing(false); }}
        eyebrow="Product"
        title={selected?.name}
        icon={Package}
        actions={selected && (editing ? (
          <>
            <Button data-testid="detail-product-cancel" variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button data-testid="detail-product-save" size="sm" onClick={saveEdit} disabled={savingEdit} className="bg-blue-600 hover:bg-blue-700">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </>
        ) : (
          <Button data-testid="detail-product-edit" variant="outline" size="sm" onClick={startEdit}>
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
        ))}
      >
        {selected && !editing && (
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="h-24 w-24 rounded-xl bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0">
                {selected.image_url ? <img src={selected.image_url} alt="" className="h-full w-full object-cover" /> : <Package className="h-8 w-8 m-8 text-slate-400" />}
              </div>
              <div>
                {(() => { const s = STATUS_BADGE[selected.status] || STATUS_BADGE[selected.active ? "active" : "inactive"]; return <Badge variant={s.variant}>{s.label}</Badge>; })()}
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">{gbp(selected.price_incl_vat)}</p>
                <p className="text-xs text-slate-400">incl. {selected.vat_rate}% VAT</p>
              </div>
            </div>
            {selected.images?.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="product-detail-gallery">
                {selected.images.map((url) => (
                  <img key={url} src={url} alt="" className={`h-16 w-16 rounded-lg object-cover border-2 ${selected.image_url === url ? "border-blue-600" : "border-slate-200 dark:border-slate-700"}`} />
                ))}
              </div>
            )}
            {selected.description && <p className="text-sm text-slate-600 dark:text-slate-300">{selected.description}</p>}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["SKU", selected.sku],
                ["Barcode", selected.barcode || "—"],
                ["Category", selected.category],
                ["Cost price", gbp(selected.cost_price)],
                ["Selling price (excl VAT)", gbp(selected.selling_price)],
                ["VAT rate", `${selected.vat_rate}%`],
                ["Pack size", `${selected.pack_size || 1} units/pack`],
                ["Price incl VAT", gbp(selected.price_incl_vat)],
                ["Stock controlled", selected.stock_controlled ? "Yes" : "No"],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col">
                  <span className="text-xs uppercase tracking-wide text-slate-400">{k}</span>
                  <span className="font-medium text-slate-900 dark:text-white">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {selected && editing && (
          <div className="space-y-4" data-testid="detail-product-edit-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="detail-edit-name" value={editForm.name || ""} onChange={(e) => eupd("name", e.target.value)} className="mt-1" /></div>
              <div><Label>SKU</Label><Input data-testid="detail-edit-sku" value={editForm.sku || ""} onChange={(e) => eupd("sku", e.target.value)} className="mt-1" /></div>
              <div><Label>Barcode</Label><Input value={editForm.barcode || ""} onChange={(e) => eupd("barcode", e.target.value)} className="mt-1" /></div>
              <div>
                <Label>Category</Label>
                <Select value={editForm.category} onValueChange={(v) => eupd("category", v)}>
                  <SelectTrigger data-testid="detail-edit-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {catOptions(editForm.category).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>VAT</Label><Select value={vatMode(editForm.vat_rate)} onValueChange={(m) => setVat(m, true)}><SelectTrigger data-testid="edit-vat-mode" className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="standard">Standard VAT ({stdVat}%)</SelectItem><SelectItem value="zero">Zero VAT Rated (0%)</SelectItem></SelectContent></Select></div>
              <div><Label>Cost price (£)</Label><Input type="number" step="0.01" value={editForm.cost_price} onChange={(e) => eupd("cost_price", e.target.value)} className="mt-1" /></div>
              <div><Label>Selling excl VAT (£)</Label><Input data-testid="detail-edit-price" type="number" step="0.01" value={editForm.selling_price} onChange={(e) => eupd("selling_price", e.target.value)} className="mt-1" /></div>
              <div><Label>Pack size (units/case)</Label><Input type="number" min={1} value={editForm.pack_size} onChange={(e) => eupd("pack_size", e.target.value)} className="mt-1" /></div>
              <div className="col-span-2 text-sm text-slate-500">Price incl VAT: <span className="font-semibold text-slate-900 dark:text-white">{gbp(editIncl)}</span></div>
              <div className="col-span-2"><Label>Description</Label><Textarea value={editForm.description || ""} onChange={(e) => eupd("description", e.target.value)} className="mt-1" /></div>
              <div className="col-span-2">
                <Label>Images</Label>
                <div className="mt-1"><ImageManager images={editForm.images} mainUrl={editForm.image_url} onChange={(imgs, main) => setEditForm((f) => ({ ...f, images: imgs, image_url: main }))} testid="detail-edit-img" /></div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => eupd("status", v)}>
                  <SelectTrigger data-testid="detail-edit-status" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2"><Switch checked={editForm.stock_controlled} onCheckedChange={(v) => eupd("stock_controlled", v)} /><Label>Stock controlled</Label></div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
              <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              <Button data-testid="detail-edit-save" onClick={saveEdit} disabled={savingEdit} className="bg-blue-600 hover:bg-blue-700">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
            </div>
          </div>
        )}
      </DetailPanel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU</Label><Input data-testid="product-form-sku" value={form.sku} onChange={(e) => upd("sku", e.target.value)} className="mt-1" /></div>
            <div><Label>Barcode</Label><Input data-testid="product-form-barcode" value={form.barcode} onChange={(e) => upd("barcode", e.target.value)} className="mt-1" /></div>
            <div className="col-span-2"><Label>Name</Label><Input data-testid="product-form-name" value={form.name} onChange={(e) => upd("name", e.target.value)} className="mt-1" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger data-testid="product-form-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {catOptions(form.category).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>VAT</Label><Select value={vatMode(form.vat_rate)} onValueChange={(m) => setVat(m, false)}><SelectTrigger data-testid="product-form-vat" className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="standard">Standard VAT ({stdVat}%)</SelectItem><SelectItem value="zero">Zero VAT Rated (0%)</SelectItem></SelectContent></Select></div>
            <div><Label>Cost price (£)</Label><Input data-testid="product-form-cost" type="number" step="0.01" value={form.cost_price} onChange={(e) => upd("cost_price", e.target.value)} className="mt-1" /></div>
            <div><Label>Selling price excl VAT (£)</Label><Input data-testid="product-form-price" type="number" step="0.01" value={form.selling_price} onChange={(e) => upd("selling_price", e.target.value)} className="mt-1" /></div>
            <div className="col-span-2 text-sm text-slate-500">Price incl VAT: <span className="font-semibold text-slate-900 dark:text-white">{gbp(inclPreview)}</span></div>
            <div className="col-span-2"><Label>Description</Label><Textarea data-testid="product-form-description" value={form.description} onChange={(e) => upd("description", e.target.value)} className="mt-1" /></div>
            <div className="col-span-2">
              <Label>Images</Label>
              <div className="mt-1"><ImageManager images={form.images} mainUrl={form.image_url} onChange={(imgs, main) => setForm((f) => ({ ...f, images: imgs, image_url: main }))} testid="product-form-img" /></div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                <SelectTrigger data-testid="product-form-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 rounded-lg border border-slate-200 dark:border-slate-800 p-3 mt-1" data-testid="product-form-stock-section">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Product Stock</p>
              <div className="grid grid-cols-2 gap-3 items-center">
                <div><Label>Units per box/pack</Label><Input data-testid="product-form-pack-size" type="number" min={1} value={form.pack_size} onChange={(e) => upd("pack_size", e.target.value)} placeholder="e.g. 24" className="mt-1" /></div>
                <label className="flex items-center gap-2 text-sm mt-5"><Switch data-testid="product-form-stock-controlled" checked={form.stock_controlled} onCheckedChange={(v) => upd("stock_controlled", v)} /> Stock controlled</label>
              </div>
              <p className="text-xs text-slate-400 mt-2">Units per pack is used to calculate how many packs are needed per building from planogram totals.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="product-form-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="product-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Please confirm</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <span className="font-semibold text-slate-900 dark:text-white">{toDelete?.name}</span>{toDelete?.sku ? ` (${toDelete.sku})` : ""}? This permanently removes the product and its kiosk assignments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="product-delete-confirm" onClick={(e) => { e.preventDefault(); confirmDelete(); }} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">
              Delete product
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toClear} onOpenChange={(o) => { if (!o) setToClear(null); }}>
        <AlertDialogContent data-testid="product-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete for good?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <span className="font-semibold text-slate-900 dark:text-white">{toClear?.name}</span>{toClear?.sku ? ` (${toClear.sku})` : ""} and its kiosk assignments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-clear-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="product-clear-confirm" onClick={(e) => { e.preventDefault(); clearOne(); }} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">Delete for good</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearAllOpen} onOpenChange={setClearAllOpen}>
        <AlertDialogContent data-testid="product-clear-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Empty the deleted list?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete all {deleted.length} product(s) in the deleted list and their kiosk assignments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="product-clear-all-confirm" onClick={(e) => { e.preventDefault(); clearAll(); }} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">Clear all for good</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
